import type { ChangeOrganizationUserRoleRequest } from '@compass/client/types/organization_user';
import type {
  AddProgramUserRoleRequest,
  ChangeProgramUserRoleRequest,
  RemoveProgramUserRoleRequest,
} from '@compass/client/types/program_user';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { permissionMapQueryKey } from '@/hooks/usePermissions';
import { queryKeys as programUsersQueryKeys } from '@/hooks/useProgramUsers';
import { organizationUserService, programUserService } from '@/services/api';

const queryKeys = {
  userRoles: {
    all: ['compass', 'userRoles'] as const,
    organization: (organizationId: string, userId: string) =>
      [...queryKeys.userRoles.all, 'organization', organizationId, userId] as const,
  },
};

/**
 * Query hook for fetching user roles within an organization using
 * OrganizationUserRoleService.GetOrganizationUserRoles.
 * Use this when you need roles scoped to a specific organization context.
 */
export const useOrganizationUserRoles = (organizationId?: string, userId?: string) => {
  return useQuery({
    queryKey: queryKeys.userRoles.organization(organizationId || '', userId || ''),
    queryFn: () => {
      if (!organizationId || !userId) throw new Error('Organization ID and User ID are required');
      return organizationUserService.getOrganizationUserRoles({
        organization_id: organizationId,
        user_id: userId,
      });
    },
    enabled: !!organizationId && !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for atomically changing a user's role at organization level.
 * This replaces the existing role with a new one in a single operation.
 */
export const useChangeOrganizationUserRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChangeOrganizationUserRoleRequest) =>
      organizationUserService.changeOrganizationUserRole(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.userRoles.organization(
          variables.organization_id || '',
          variables.user_id || '',
        ),
      });
      queryClient.invalidateQueries({ queryKey: permissionMapQueryKey });
    },
  });
};

/**
 * Mutation hook for adding a role to a user at program level
 */
export const useAddProgramUserRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: AddProgramUserRoleRequest) =>
      programUserService.addProgramUserRole(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.userRoles.organization(
          variables.organization_id || '',
          variables.user_id || '',
        ),
      });
      queryClient.invalidateQueries({ queryKey: permissionMapQueryKey });

      if (variables.organization_id && variables.program_id) {
        queryClient.invalidateQueries({
          queryKey: programUsersQueryKeys.programUsers.searchAll(),
        });
      }
    },
  });
};

/**
 * Mutation hook for removing a role from a user at program level
 */
export const useRemoveProgramUserRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: RemoveProgramUserRoleRequest) =>
      programUserService.removeProgramUserRole(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.userRoles.organization(
          variables.organization_id || '',
          variables.user_id || '',
        ),
      });
      queryClient.invalidateQueries({ queryKey: permissionMapQueryKey });

      if (variables.organization_id && variables.program_id) {
        queryClient.invalidateQueries({
          queryKey: programUsersQueryKeys.programUsers.searchAll(),
        });
      }
    },
  });
};

/**
 * Mutation hook for atomically changing a user's role at program level.
 * This replaces the existing role with a new one in a single operation.
 */
export const useChangeProgramUserRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChangeProgramUserRoleRequest) =>
      programUserService.changeProgramUserRole(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.userRoles.organization(
          variables.organization_id || '',
          variables.user_id || '',
        ),
      });
      queryClient.invalidateQueries({ queryKey: permissionMapQueryKey });

      if (variables.organization_id && variables.program_id && variables.user_id) {
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.userRoles.all,
            'program',
            variables.organization_id,
            variables.program_id,
            variables.user_id,
          ],
        });
      }

      // Invalidate program users list to refetch
      if (variables.organization_id && variables.program_id) {
        queryClient.invalidateQueries({
          queryKey: programUsersQueryKeys.programUsers.search(
            variables.organization_id,
            variables.program_id,
          ),
        });
      }
    },
  });
};
